import type { Channel } from "@hybrid-iptv/types";
import { genreColor } from "./genreColors.js";
import type { ChannelProgramIndex } from "./hooks.js";
import { tileLetter } from "../library/tileLetter.js";

interface PreviewInfoProps {
  channel: Channel | null;
  programIndex: ChannelProgramIndex | null;
}

/**
 * Right-side info panel in the traditional preview. Shows the current
 * programme's details or falls back to channel identification when there is
 * no EPG data (or no channel selected at all).
 */
export function PreviewInfo({ channel, programIndex }: PreviewInfoProps) {
  if (!channel) {
    return (
      <div className="epg-preview-info">
        <div className="epg-preview-info__placeholder">
          <span className="epg-preview-info__logo">S</span>
          <span className="epg-preview-info__hint">Select a channel to preview</span>
        </div>
      </div>
    );
  }

  const nowPlaying = programIndex?.getNowPlaying(channel.id) ?? null;
  const hasData = programIndex?.hasData(channel.id) ?? false;

  if (!nowPlaying || !hasData) {
    return (
      <div className="epg-preview-info">
        <div className="epg-preview-info__channel">
          {channel.logoUrl ? (
            <img className="epg-preview-info__logo-img" src={channel.logoUrl} alt="" />
          ) : (
            <span className="epg-preview-info__logo">{tileLetter(channel.name, "S")}</span>
          )}
          <div className="epg-preview-info__channel-meta">
            <span className="epg-preview-info__channel-number">
              {channel.number !== undefined ? `CH ${channel.number}` : ""}
            </span>
            <span className="epg-preview-info__channel-name">{channel.name}</span>
          </div>
        </div>
        <span className="epg-preview-info__no-data">No programme information available</span>
      </div>
    );
  }

  const color = genreColor(nowPlaying.genre);
  const start = formatDateTime(nowPlaying.start);
  const end = formatDateTime(nowPlaying.end);
  const now = new Date();
  const isLive = now >= new Date(nowPlaying.start) && now < new Date(nowPlaying.end);
  const progress = isLive
    ? Math.round(
        ((now.getTime() - new Date(nowPlaying.start).getTime()) /
          (new Date(nowPlaying.end).getTime() - new Date(nowPlaying.start).getTime())) *
          100,
      )
    : 0;

  return (
    <div className="epg-preview-info">
      <div className="epg-preview-info__header">
        {channel.logoUrl ? (
          <img className="epg-preview-info__logo-img" src={channel.logoUrl} alt="" />
        ) : (
          <span className="epg-preview-info__logo">{tileLetter(channel.name, "S")}</span>
        )}
        <div className="epg-preview-info__channel-meta">
          <span className="epg-preview-info__channel-number">
            {channel.number !== undefined ? `CH ${channel.number}` : ""}
          </span>
          <span className="epg-preview-info__channel-name">{channel.name}</span>
        </div>
        {nowPlaying.genre && (
          <span className="epg-preview-info__genre" style={{ color }}>
            {nowPlaying.genre}
          </span>
        )}
      </div>
      <h3 className="epg-preview-info__title">{nowPlaying.title}</h3>
      {nowPlaying.subtitle && (
        <p className="epg-preview-info__subtitle">{nowPlaying.subtitle}</p>
      )}
      <div className="epg-preview-info__time">
        <span>
          {start} – {end}
        </span>
        {isLive && <span className="epg-preview-info__live">LIVE</span>}
      </div>
      {isLive && (
        <div className="epg-preview-info__progress">
          <div
            className="epg-preview-info__progress-bar"
            style={{ width: `${progress}%`, backgroundColor: color }}
          />
        </div>
      )}
      {nowPlaying.description && (
        <p className="epg-preview-info__desc">{nowPlaying.description}</p>
      )}
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}
